import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

import notificationChimeUrl from '@/assets/sounds/notification.mp3';
import { useAuth } from '@/contexts/auth-context';
import { useOrganization } from '@/contexts/organization-context';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook to get user's notification sound preference
 */
export function useNotificationSoundPreference() {
  const { user } = useAuth();
  const { currentOrganizationId } = useOrganization();

  // Skip query when in admin mode (currentOrganizationId === 'admin')
  const isValidOrgId = currentOrganizationId && currentOrganizationId !== 'admin';

  return useQuery({
    queryKey: ['notification-preferences', 'sound', currentOrganizationId, user?.id],
    queryFn: async () => {
      if (!user?.id || !isValidOrgId) {
        return { sound_enabled: true }; // Default to enabled
      }

      const { data, error } = await supabase
        .from('notification_preferences')
        .select('sound_enabled')
        .eq('user_id', user.id)
        .eq('organization_id', currentOrganizationId)
        .maybeSingle();

      if (error) {
        return { sound_enabled: true }; // Default to enabled on error
      }

      // If no preferences exist, default to enabled
      return data || { sound_enabled: true };
    },
    enabled: !!user?.id && !!isValidOrgId,
    staleTime: 60 * 1000, // Cache for 1 minute
  });
}

/**
 * Hook to play notification sounds
 * Returns a function that plays a notification sound if user has sounds enabled
 */
export function useNotificationSound() {
  const { data: preferences } = useNotificationSoundPreference();

  return useCallback(() => {
    // Only play if user has sounds enabled
    if (!preferences?.sound_enabled) {
      return;
    }

    // Check browser compatibility
    if (typeof Audio === 'undefined') {
      return;
    }

    try {
      const audio = new Audio(notificationChimeUrl);
      audio.volume = 0.5;
      audio.play().catch(() => {
        // play() rejects when the browser autoplay policy blocks playback
        // (no prior user gesture) - silently skip, sound is not critical
      });
    } catch {
      // Silently fail - sound is not critical functionality
    }
  }, [preferences?.sound_enabled]);
}
